import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { type ExamType, type ExamLevel } from "@/types/simulation-results";
import { useSimulationResults } from "@/hooks/useSimulationResults";
import { SimulationPerformanceTrendsSection } from "@/components/results/SimulationPerformanceTrendsSection";
import {
  SimulationHistorySection,
  parseFoldedModules,
  serializeFoldedModules,
  FOLDED_MODULES_SEARCH_PARAM,
  type HistoryView,
  type StatusFilter,
} from "@/components/results/SimulationHistorySection";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import {
  calculateAverageScores,
  getAttemptedModuleKeys,
  getLatestReviewTargetByModule,
  processChartData,
} from "@/lib/simulationResultsChartData";
import {
  SIMULATION_MODULE_KEYS,
  type SimulationModuleKey,
} from "@/lib/simulationResultsChartData";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { ROUTES } from "@/lib/routeConstants";
import { useNavigateToModuleReview } from "@/hooks/useNavigateToModuleReview";

const EXAM_TYPES: readonly ExamType[] = ["goethe", "osd", "telc"];
const EXAM_LEVELS: readonly ExamLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
const HISTORY_VIEWS: readonly HistoryView[] = ["simulation", "module"];
const STATUS_FILTERS: readonly StatusFilter[] = ["all", "passed", "failed"];

function readSearchParam<T extends string>(
  searchParams: URLSearchParams,
  key: string,
  allowedValues: readonly T[],
  fallback: T,
) {
  const value = searchParams.get(key);
  return value && allowedValues.includes(value as T) ? (value as T) : fallback;
}

export default function Results() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedExamType = readSearchParam(
    searchParams,
    "examType",
    ["all", ...EXAM_TYPES],
    "all",
  );
  const selectedLevel = readSearchParam(
    searchParams,
    "level",
    ["all", ...EXAM_LEVELS],
    "all",
  );
  const selectedHistoryView = readSearchParam(
    searchParams,
    "view",
    HISTORY_VIEWS,
    "simulation",
  );
  const selectedStatusFilter = readSearchParam(
    searchParams,
    "status",
    STATUS_FILTERS,
    "all",
  );
  const highlightedSessionId = searchParams.get("session");
  const moduleParam = searchParams.get("module");
  const highlightedModuleName =
    moduleParam &&
    SIMULATION_MODULE_KEYS.includes(moduleParam as SimulationModuleKey)
      ? moduleParam
      : null;
  const foldedModules = useMemo(
    () => parseFoldedModules(searchParams.get(FOLDED_MODULES_SEARCH_PARAM)),
    [searchParams],
  );

  const {
    results: filteredResults,
    isLoading,
    error,
    retryAnalysis,
  } = useSimulationResults(selectedLevel, selectedExamType);
  const { results: allExamTypeResults } = useSimulationResults(
    "all",
    selectedExamType,
  );

  const levelsWithResults = useMemo(
    () => new Set(allExamTypeResults.map((r) => r.level)),
    [allExamTypeResults],
  );

  const averageScores = useMemo(
    () => calculateAverageScores(filteredResults),
    [filteredResults],
  );

  const chartData = useMemo(
    () => processChartData(filteredResults),
    [filteredResults],
  );
  const attemptedModules = useMemo(
    () => getAttemptedModuleKeys(filteredResults),
    [filteredResults],
  );
  const latestReviewByModule = useMemo(
    () => getLatestReviewTargetByModule(filteredResults),
    [filteredResults],
  );

  const setResultsParam = useCallback(
    (key: string, value: string, defaultValue: string) => {
      const nextParams = new URLSearchParams(searchParams);

      if (value === defaultValue) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // If a level is selected via URL but has no results for the current exam type, reset to 'all'.
  useEffect(() => {
    if (
      selectedLevel !== "all" &&
      !isLoading &&
      !levelsWithResults.has(selectedLevel)
    ) {
      setResultsParam("level", "all", "all");
    }
  }, [selectedLevel, isLoading, levelsWithResults, setResultsParam]);

  const handleFoldedModulesChange = useCallback(
    (folded: ReadonlySet<SimulationModuleKey>) => {
      const nextParams = new URLSearchParams(searchParams);
      const serialized = serializeFoldedModules(folded);

      if (serialized) {
        nextParams.set(FOLDED_MODULES_SEARCH_PARAM, serialized);
      } else {
        nextParams.delete(FOLDED_MODULES_SEARCH_PARAM);
      }

      // If the deep-linked module is being folded, clear highlight params so
      // containsDeepLinkHighlight no longer forces it back open.
      if (
        highlightedModuleName &&
        folded.has(highlightedModuleName as SimulationModuleKey)
      ) {
        nextParams.delete("session");
        nextParams.delete("module");
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams, highlightedModuleName],
  );

  const navigateToModuleReview = useNavigateToModuleReview({
    returnBasePath: ROUTES.RESULTS,
    mode: "results",
  });

  const handleModuleClick = navigateToModuleReview;

  const handleModuleRetry = useCallback(
    async (resultId: string, moduleName: string) => {
      try {
        await retryAnalysis(resultId, moduleName);
      } catch (retryError) {
        console.error("Failed to retry module analysis:", retryError);
      }
    },
    [retryAnalysis],
  );

  const handleModuleReviewFromSummary = useCallback(
    (sessionId: string, module: SimulationModuleKey) => {
      navigateToModuleReview(sessionId, module);
    },
    [navigateToModuleReview],
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center p-12">
          <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
          <p className="text-muted-foreground animate-pulse">
            {t("common.loading")}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="p-4 rounded-full bg-destructive/10 mb-4">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">
            {t("simulation.results.errors.unableToLoad")}
          </h2>
          <p className="text-muted-foreground max-w-md mb-6">{error.message}</p>
          <Button onClick={() => globalThis.location.reload()}>
            {t("simulation.results.errors.tryAgain")}
          </Button>
        </div>
      );
    }

    return (
      <>
        <SimulationPerformanceTrendsSection
          chartData={chartData}
          attemptedModules={attemptedModules}
          averageScores={averageScores}
          gradientIdPrefix="results"
          latestReviewByModule={latestReviewByModule}
          onModuleReviewClick={handleModuleReviewFromSummary}
        />

        <SimulationHistorySection
          results={filteredResults}
          highlightedSessionId={highlightedSessionId}
          highlightedModuleName={highlightedModuleName}
          onModuleClick={handleModuleClick}
          onModuleRetry={handleModuleRetry}
          view={selectedHistoryView}
          statusFilter={selectedStatusFilter}
          onViewChange={(value) => setResultsParam("view", value, "simulation")}
          onStatusFilterChange={(value) =>
            setResultsParam("status", value, "all")
          }
          foldedModules={foldedModules}
          onFoldedModulesChange={handleFoldedModulesChange}
        />
      </>
    );
  };

  return (
    <StandardPageShell
      title={t("simulation.results.title")}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
    >
      <p className="mb-6 text-sm text-muted-foreground">
        {t("simulation.results.subtitle")}
      </p>
      <div>
        {/* Filters */}
        <Card className="mb-5 overflow-hidden sm:mb-8">
          <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
            <CardTitle className="text-lg">
              {t("simulation.history.filterResults")}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {t("simulation.history.details")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <Tabs
              value={selectedExamType}
              onValueChange={(value) =>
                setResultsParam("examType", value, "all")
              }
              className="w-full"
            >
              <div className="overflow-x-auto scrollbar-hide -mx-1 px-1 mb-4">
                <TabsList className="inline-flex w-auto min-w-full justify-start sm:grid sm:w-full sm:grid-cols-4 whitespace-nowrap">
                  <TabsTrigger value="all" className="px-4 py-2">
                    {t("simulation.results.filter.allTypes", {
                      defaultValue: "All Types",
                    })}
                  </TabsTrigger>
                  <TabsTrigger value="goethe" className="px-4 py-2">
                    Goethe
                  </TabsTrigger>
                  <TabsTrigger value="osd" className="px-4 py-2">
                    ÖSD
                  </TabsTrigger>
                  <TabsTrigger value="telc" className="px-4 py-2">
                    telc
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {t("partnerFinder.search.levelLabel")}:
                  </span>
                  <Select
                    value={selectedLevel}
                    onValueChange={(value) =>
                      setResultsParam("level", value, "all")
                    }
                  >
                    <SelectTrigger className="min-w-0 flex-1 sm:w-[180px] sm:flex-none">
                      <SelectValue
                        placeholder={t("partnerFinder.search.levelLabel")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("simulation.results.filter.allLevels", {
                          defaultValue: "All Levels",
                        })}
                      </SelectItem>
                      {EXAM_LEVELS.filter((level) =>
                        levelsWithResults.has(level),
                      ).map((level) => (
                        <SelectItem key={level} value={level}>
                          {level} - {t(`levelSelection.levels.${level}.name`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {(selectedExamType !== "all" || selectedLevel !== "all") && (
                  <Badge variant="secondary" className="w-fit">
                    {t("simulation.results.history.resultsFound", {
                      count: filteredResults.length,
                    })}
                  </Badge>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {renderContent()}
      </div>
    </StandardPageShell>
  );
}
